import React from 'react';
import { useSessionForm } from './useSessionForm';
import CityAutocomplete from '../components/CityAutocomplete';
import WarehouseAutocomplete from '../components/WarehouseAutocomplete';
import Input from '../components/Input';
import Button from '../components/Button';
import { useLocalization } from '../localization/LocalizationContext';
import { notifyPaymentInitiated } from './sessionApi';
import Countdown from './Countdown';

interface Props {
  orderId: string;
  itemName: string;
  amount: number;
  /** null once there is no live reservation deadline — render no countdown. */
  expiresAt: number | null;
  onExpire: () => void;
}

/**
 * The API's error codes are machine-readable ('expired', 'sold', 'HTTP 500').
 * Mapped here rather than in src/localization, which is frozen for this branch.
 */
const SUBMIT_ERROR_TEXT: Record<string, string> = {
  expired: 'Час бронювання вийшов. Натисніть КУПИТИ в каналі ще раз, якщо товар досі доступний.',
  sold: 'На жаль, цей товар уже продано.',
  not_found: 'Замовлення не знайдено. Напишіть продавцю в Telegram.',
  invalid_shipping: 'Перевірте, будь ласка, місто, відділення, ім’я та телефон.',
  cancelled: 'Це замовлення скасовано. Напишіть продавцю в Telegram.',
  not_shipped: 'Спочатку заповніть адресу доставки.',
  // Their payment is already recorded — resubmitting the form would strip the
  // proof timers off a paid order. Send them to the bot with the receipt.
  already_paid:
    'Вашу оплату вже зафіксовано — заповнювати форму знову не потрібно. Надішліть, будь ласка, скріншот або квитанцію боту в Telegram.',
  internal: 'Не вдалося зберегти замовлення. Спробуйте ще раз за хвилину.',
};

const GENERIC_SUBMIT_ERROR =
  'Не вдалося зберегти замовлення. Спробуйте ще раз або напишіть продавцю в Telegram.';

export function submitErrorText(code: string): string {
  return SUBMIT_ERROR_TEXT[code] ?? GENERIC_SUBMIT_ERROR;
}

const SessionForm: React.FC<Props> = ({ orderId, itemName, amount, expiresAt, onExpire }) => {
  const { t } = useLocalization();

  const {
    formData,
    errors,
    cities,
    warehouses,
    isLoading,
    isSubmitting,
    step,
    isSuccess,
    payUrl,
    submitError,
    handleInputChange,
    handleCitySearch,
    handleWarehouseSearch,
    validateField,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    handlePopularCitySelect,
  } = useSessionForm(orderId);

  // The classes Button renders for its primary variant, reproduced here so the
  // anchor below is visually identical to the button it replaced.
  const payButtonClasses =
    'font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ' +
    'transition-colors duration-200 min-w-[120px] text-center inline-block ' +
    'bg-black hover:bg-gray-800 text-white';

  /**
   * Fire-and-forget, deliberately NOT awaited. This runs during the anchor's
   * own click, so the browser navigates as a direct result of the user's tap.
   * Awaiting a fetch first and then calling window.open puts the open outside
   * the user-gesture window, and mobile Safari and Telegram's in-app WKWebView
   * block that popup silently — which is the normal case here, since buyers
   * arrive from a bot DM. notifyPaymentInitiated swallows its own errors.
   *
   * The null guard mirrors the early return the old button had: with no payUrl
   * there is nowhere to go, so cancel the navigation rather than opening a
   * blank tab, and do not report a payment that cannot have started.
   * Unreachable today (this only renders once the response carrying payUrl has
   * arrived), which is exactly why it is worth keeping explicit.
   */
  const handlePayClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!payUrl) {
      e.preventDefault();
      return;
    }
    void notifyPaymentInitiated(orderId);
  };

  const renderStepIndicator = () => {
    return (
      <div className="mb-12 max-w-4xl mx-auto">
        <div className="flex items-start justify-between relative px-4">
          {/* Line connecting the steps */}
          <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 -z-10"></div>

          {/* Step 1 */}
          <div className="flex flex-col items-center w-24 sm:w-28">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 1 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">1</span>
            </div>
            <div className="text-base font-medium text-center">{t.steps.city}</div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center w-24 sm:w-28">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 2 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">2</span>
            </div>
            <div className="text-base font-medium text-center">{t.steps.warehouse}</div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col items-center w-24 sm:w-32">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 3 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">3</span>
            </div>
            <div className="text-base font-medium text-center hyphens-auto">{t.steps.contactInfo}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedData = () => {
    // Don't show anything on step 1
    if (step === 1) return null;

    return (
      <div className="max-w-3xl mx-auto mb-6">
        <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.city && (
              <div>
                <p className="text-sm text-gray-600">{t.warehouseStep.selectedCity} <span className="font-medium text-gray-800">{formData.city.Description}</span></p>
              </div>
            )}
            {formData.warehouse && step >= 3 && (
              <div>
                <p className="text-sm text-gray-600">{t.contactStep.selectedWarehouse} <span className="font-medium text-gray-800">{formData.warehouse.Description}</span></p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.cityStep.title}</h2>

            <CityAutocomplete
              cities={cities}
              isLoading={isLoading}
              onSearch={handleCitySearch}
              onSelect={(city) => {
                // First update the form data with the selected city
                handleInputChange('city', city);

                // Automatically proceed to the next step after a delay
                setTimeout(() => {
                  // Only proceed if city is valid
                  if (city && city.Ref) {
                    // Skip validation since we know the city is valid
                    handleNextStep(true);
                  }
                }, 300);
              }}
              selectedCity={formData.city}
              error={errors.city}
              onPopularCitySelect={handlePopularCitySelect}
            />
          </div>
        );
      case 2:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.warehouseStep.title}</h2>

            <WarehouseAutocomplete
              warehouses={warehouses}
              isLoading={isLoading}
              onSelect={(warehouse) => {
                // First update the form data with the selected warehouse
                handleInputChange('warehouse', warehouse);

                // Automatically proceed to the next step after a longer delay
                // to ensure error state is cleared properly
                setTimeout(() => {
                  // Only proceed if warehouse is valid
                  if (warehouse && warehouse.Ref) {
                    // Skip validation since we know the warehouse is valid
                    handleNextStep(true);
                  }
                }, 300); // Increased delay to allow error clearing to take effect
              }}
              selectedWarehouse={formData.warehouse}
              error={errors.warehouse}
              onSearch={handleWarehouseSearch}
            />
          </div>
        );
      case 3:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.contactStep.title}</h2>

            <Input
              label={t.contactStep.fullName.label}
              type="text"
              placeholder={t.contactStep.fullName.placeholder}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={() => validateField('name')}
              error={errors.name}
              icon={
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              }
            />

            <Input
              label={t.contactStep.phone.label}
              type="tel"
              placeholder={t.contactStep.phone.placeholder}
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={() => validateField('phone')}
              error={errors.phone}
              icon={
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              }
            />

            {(errors.submit || submitError) && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm" data-testid="submit-error">
                      {errors.submit || (submitError ? submitErrorText(submitError) : null)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderNavButtons = () => {
    return (
      <div className="flex justify-between mt-8 pt-4 border-t border-gray-200 max-w-3xl mx-auto">
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={handlePrevStep}
            type="button"
            className="form-button"
          >
            <span className="flex items-center">
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              {t.buttons.back}
            </span>
          </Button>
        ) : (
          <div></div>
        )}

        {step < 3 ? (
          <Button
            onClick={() => handleNextStep(false)}
            type="button"
            className="form-button"
          >
            <span className="flex items-center">
              {t.buttons.next}
              <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            type="button"
            isLoading={isSubmitting}
            className="form-button"
          >
            {t.buttons.submit}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full" data-testid="session-form">
      {isSuccess ? (
        <div className="text-center py-8 max-w-3xl mx-auto bg-white shadow-sm rounded-lg border border-gray-100">
          {/* Julia Image */}
          <div className="my-6 flex justify-center">
            <img
              src="/images/julia.jpg"
              alt="Julia"
              className="rounded-lg shadow-md max-w-full"
              style={{ maxHeight: '300px' }}
            />
          </div>
          <p className="mt-2 text-sm text-black-500 mb-6">{t.success.message}</p>
          <p>Після оплати надішліть скріншот або квитанцію боту в Telegram.</p>
          {/* Payment button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <a
              href={payUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePayClick}
              className={payButtonClasses}
              data-testid="pay-link"
            >
              {t.payment.payWithMonobank}
            </a>
          </div>
        </div>
      ) : (
        <div>
          {expiresAt !== null && <Countdown expiresAt={expiresAt} onExpire={onExpire} />}

          {renderStepIndicator()}

          {/* Persistent cart item display */}
          <div className="max-w-3xl mx-auto mb-6">
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-custom-pink bg-opacity-10 p-3 rounded-md">
                  <svg className="h-6 w-6 text-custom-pink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{itemName}</h3>
                  <p className="text-sm text-gray-500">
                    {t.payment.amountInfo.replace('{amount}', String(amount))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Selected data display */}
          {renderSelectedData()}

          {renderStepContent()}
          {renderNavButtons()}
        </div>
      )}
    </div>
  );
};

export default SessionForm;
